import { skyLeviathanTriangleSweep } from './96-sky-leviathan-contacts.mjs';
// Numeric glTF animation/contact owner. No renderer, wall clock or source I/O.
const accepted = new WeakSet();
const poses = new WeakSet();
const lengths = { position: 3, quaternion: 4, scale: 3 };
const identity = () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
const vector = (value, length, limit = 1e6) =>
  (Array.isArray(value) || ArrayBuffer.isView(value)) &&
  value.length === length &&
  Array.from(value).every((n) => Number.isFinite(n) && Math.abs(n) <= limit);
const indexOf = (n, length) => Number.isInteger(n) && n >= 0 && n < length;
const freeze = (value) => {
  if (ArrayBuffer.isView(value)) value = Array.from(value);
  if (value && typeof value === 'object') {
    for (const key of Object.keys(value)) value[key] = freeze(value[key]);
    Object.freeze(value);
  }
  return value;
};
function multiply(a, b) {
  const out = new Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++)
      out[c * 4 + r] =
        a[r] * b[c * 4] +
        a[4 + r] * b[c * 4 + 1] +
        a[8 + r] * b[c * 4 + 2] +
        a[12 + r] * b[c * 4 + 3];
  return out;
}
function compose({ position: p, quaternion: q, scale: s }) {
  const [x, y, z, w] = q,
    x2 = x * 2,
    y2 = y * 2,
    z2 = z * 2;
  return [
    (1 - y * y2 - z * z2) * s[0],
    (x * y2 + w * z2) * s[0],
    (x * z2 - w * y2) * s[0],
    0,
    (x * y2 - w * z2) * s[1],
    (1 - x * x2 - z * z2) * s[1],
    (y * z2 + w * x2) * s[1],
    0,
    (x * z2 + w * y2) * s[2],
    (y * z2 - w * x2) * s[2],
    (1 - x * x2 - y * y2) * s[2],
    0,
    p[0],
    p[1],
    p[2],
    1,
  ];
}
function blend(a, b, t, quaternion) {
  if (!quaternion) return a.map((v, i) => v + (b[i] - v) * t);
  let dot = a.reduce((sum, v, i) => sum + v * b[i], 0);
  if (dot < 0) {
    b = b.map((v) => -v);
    dot = -dot;
  }
  if (dot > 0.9995) {
    const q = a.map((v, i) => v + (b[i] - v) * t),
      norm = Math.hypot(...q);
    return q.map((v) => v / norm);
  }
  const angle = Math.acos(Math.min(1, dot)),
    sine = Math.sin(angle);
  return a.map((v, i) => (v * Math.sin((1 - t) * angle) + b[i] * Math.sin(t * angle)) / sine);
}
function sampleTrack(track, seconds) {
  const { times, values, property } = track,
    size = lengths[property];
  let lo = 0,
    hi = times.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (times[mid] <= seconds) lo = mid;
    else hi = mid - 1;
  }
  const next = Math.min(lo + 1, times.length - 1);
  const t =
    track.interpolation === 'STEP' || times[next] === times[lo]
      ? 0
      : Math.max(0, Math.min(1, (seconds - times[lo]) / (times[next] - times[lo])));
  return blend(
    values.slice(lo * size, (lo + 1) * size),
    values.slice(next * size, (next + 1) * size),
    t,
    property === 'quaternion',
  );
}
function validMesh(mesh, nodeCount) {
  const count = mesh.positions?.length / 3;
  if (
    !indexOf(mesh.node, nodeCount) ||
    !Number.isInteger(count) ||
    count < 3 ||
    count > 150000 ||
    !vector(mesh.positions, count * 3, 10000) ||
    !mesh.indices?.length ||
    mesh.indices.length % 3 ||
    mesh.indices.length > 360000 ||
    !Array.from(mesh.indices).every((n) => indexOf(n, count))
  )
    return false;
  if (!mesh.joints && !mesh.weights) return true;
  return (
    vector(mesh.joints, count * 4) &&
    vector(mesh.weights, count * 4, 1) &&
    Array.isArray(mesh.jointNodes) &&
    mesh.jointNodes.length <= 512 &&
    mesh.jointNodes.every((n) => indexOf(n, nodeCount)) &&
    Array.from(mesh.joints).every((n) => indexOf(n, mesh.jointNodes.length)) &&
    Array.from({ length: count }, (_, i) => mesh.weights.slice(i * 4, i * 4 + 4)).every(
      (weights) =>
        weights.every((w) => w >= 0) && Math.abs(weights.reduce((a, b) => a + b, 0) - 1) < 0.001,
    ) &&
    vector(mesh.bindMatrix, 16) &&
    vector(mesh.bindMatrixInverse, 16) &&
    mesh.inverseBindMatrices?.length === mesh.jointNodes.length &&
    mesh.inverseBindMatrices.every((m) => vector(m, 16))
  );
}

/** Validate and copy extracted glTF data into an immutable handle. Units remain
 * source metres, +Y up. Parent indices must precede children; malformed weights,
 * nonfinite transforms, unsupported interpolation or oversized data return null.
 * Revalidating an already compiled handle returns that same immutable object. */
export function compileSkyAuthoredRig(source) {
  if (accepted.has(source)) return source;
  try {
    const n = source.nodes?.length;
    if (
      typeof source.worldId !== 'string' ||
      !n ||
      n > 1024 ||
      !Array.isArray(source.nodes) ||
      !source.nodes.every(
        (node, i) =>
          (node.parent === null || node.parent === -1 || indexOf(node.parent, i)) &&
          vector(node.position, 3, 10000) &&
          vector(node.quaternion, 4, 1) &&
          Math.abs(Math.hypot(...node.quaternion) - 1) < 0.001 &&
          vector(node.scale, 3, 1000) &&
          node.scale.every((v) => v > 0),
      ) ||
      !Array.isArray(source.meshes) ||
      !source.meshes.length ||
      source.meshes.length > 128 ||
      !source.meshes.every((mesh) => validMesh(mesh, n)) ||
      source.meshes.reduce((sum, mesh) => sum + mesh.indices.length / 3, 0) > 120000 ||
      !Array.isArray(source.clips) ||
      source.clips.length > 64 ||
      new Set(source.clips.map((clip) => clip.name)).size !== source.clips.length ||
      !source.clips.every(
        (clip) =>
          typeof clip.name === 'string' &&
          Number.isFinite(clip.duration) &&
          clip.duration > 0 &&
          clip.duration <= 120 &&
          Array.isArray(clip.tracks) &&
          clip.tracks.length <= n * 3 &&
          clip.tracks.every(
            (track) =>
              indexOf(track.node, n) &&
              Object.hasOwn(lengths, track.property) &&
              ['LINEAR', 'STEP'].includes(track.interpolation) &&
              track.times?.length > 0 &&
              track.times.length <= 10000 &&
              vector(track.times, track.times.length, 120) &&
              track.times.every(
                (t, i) => t >= 0 && t <= clip.duration + 1e-5 && (!i || t > track.times[i - 1]),
              ) &&
              vector(track.values, track.times.length * lengths[track.property], 10000) &&
              (track.property !== 'scale' || track.values.every((v) => v > 0 && v <= 1000)) &&
              (track.property !== 'quaternion' ||
                track.times.every(
                  (_, i) =>
                    Math.abs(Math.hypot(...track.values.slice(i * 4, i * 4 + 4)) - 1) < 0.001,
                )),
          ),
      ) ||
      !Array.isArray(source.sockets) ||
      source.sockets.length > 256 ||
      new Set(source.sockets.map((socket) => socket.name)).size !== source.sockets.length ||
      !source.sockets.every((socket) => typeof socket.name === 'string' && indexOf(socket.node, n))
    )
      return null;
    const rig = structuredClone(source);
    accepted.add(rig);
    rig.bounds = sampleSkyAuthoredPose(rig, null, 0).bounds;
    return freeze(rig);
  } catch {
    return null;
  }
}

// Source topology is immutable. Its hierarchy is built once at readiness, then
// exact world-space leaf bounds are refit lazily once per contacted pose. Rays
// still visit original triangles in source order, preserving equal-depth ties.
const topologies = new WeakMap(),
  posedIndexes = new WeakMap(),
  meshTopologies = new WeakMap();
function topologyFor(mesh) {
  if (topologies.has(mesh)) return topologies.get(mesh);
  const order = Array.from({ length: mesh.indices.length / 3 }, (_, i) => i * 3);
  const centres = order.map((offset) =>
    [0, 1, 2].map(
      (axis) =>
        (mesh.positions[mesh.indices[offset] * 3 + axis] +
          mesh.positions[mesh.indices[offset + 1] * 3 + axis] +
          mesh.positions[mesh.indices[offset + 2] * 3 + axis]) /
        3,
    ),
  );
  const nodes = [];
  function build(start, end) {
    const id = nodes.length,
      node = { start, end, left: -1, right: -1 };
    nodes.push(node);
    if (end - start <= 12) {
      const vertices = new Set();
      for (let i = start; i < end; i++)
        for (let j = 0; j < 3; j++) vertices.add(mesh.indices[order[i] + j] * 3);
      node.vertices = Uint32Array.from(vertices);
      return id;
    }
    const min = [Infinity, Infinity, Infinity],
      max = [-Infinity, -Infinity, -Infinity];
    for (let i = start; i < end; i++)
      for (let axis = 0; axis < 3; axis++) {
        const value = centres[order[i] / 3][axis];
        min[axis] = Math.min(min[axis], value);
        max[axis] = Math.max(max[axis], value);
      }
    let axis = 0;
    for (let i = 1; i < 3; i++) if (max[i] - min[i] > max[axis] - min[axis]) axis = i;
    const sorted = order
      .slice(start, end)
      .sort((a, b) => centres[a / 3][axis] - centres[b / 3][axis] || a - b);
    for (let i = 0; i < sorted.length; i++) order[start + i] = sorted[i];
    const middle = (start + end) >>> 1;
    node.left = build(start, middle);
    node.right = build(middle, end);
    return id;
  }
  build(0, order.length);
  const value = { nodes, order: Uint32Array.from(order) };
  topologies.set(mesh, value);
  return value;
}
function contactIndex(mesh) {
  if (posedIndexes.has(mesh)) return posedIndexes.get(mesh);
  const { nodes, order } = meshTopologies.get(mesh),
    bounds = new Float64Array(nodes.length * 6);
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i],
      at = i * 6;
    for (let axis = 0; axis < 3; axis++) {
      bounds[at + axis] = Infinity;
      bounds[at + axis + 3] = -Infinity;
    }
    if (node.left < 0) {
      for (const offset of node.vertices) {
        for (let axis = 0; axis < 3; axis++) {
          const value = mesh.vertices[offset + axis];
          bounds[at + axis] = Math.min(bounds[at + axis], value);
          bounds[at + axis + 3] = Math.max(bounds[at + axis + 3], value);
        }
      }
    } else
      for (let axis = 0; axis < 3; axis++) {
        bounds[at + axis] = Math.min(bounds[node.left * 6 + axis], bounds[node.right * 6 + axis]);
        bounds[at + axis + 3] = Math.max(
          bounds[node.left * 6 + axis + 3],
          bounds[node.right * 6 + axis + 3],
        );
      }
  }
  const index = { nodes, order, bounds };
  posedIndexes.set(mesh, index);
  return index;
}
function candidates(mesh, a, delta, limit, radius) {
  const { nodes, order, bounds } = contactIndex(mesh),
    result = [],
    stack = [0];
  while (stack.length) {
    const id = stack.pop(),
      node = nodes[id],
      at = id * 6;
    let low = 0,
      high = limit;
    for (let axis = 0; axis < 3; axis++) {
      const min = bounds[at + axis] - radius,
        max = bounds[at + axis + 3] + radius;
      if (Math.abs(delta[axis]) < 1e-12) {
        if (a[axis] < min || a[axis] > max) {
          high = -1;
          break;
        }
      } else {
        const first = (min - a[axis]) / delta[axis],
          last = (max - a[axis]) / delta[axis];
        low = Math.max(low, Math.min(first, last));
        high = Math.min(high, Math.max(first, last));
        if (low > high) break;
      }
    }
    if (low > high) continue;
    if (node.left < 0) for (let j = node.start; j < node.end; j++) result.push(order[j]);
    else {
      stack.push(node.left);
      stack.push(node.right);
    }
  }
  return result.sort((a, b) => a - b);
}

const numericMeshes = new WeakMap();
function numericMesh(mesh) {
  if (numericMeshes.has(mesh)) return numericMeshes.get(mesh);
  const rigid = new Int16Array(mesh.positions.length / 3).fill(-1);
  if (mesh.joints)
    for (let i = 0; i < rigid.length; i++) {
      for (let j = 0; j < 4; j++)
        if (
          mesh.weights[i * 4 + j] === 1 &&
          [0, 1, 2, 3].every((k) => k === j || mesh.weights[i * 4 + k] === 0)
        )
          rigid[i] = mesh.joints[i * 4 + j];
    }
  const result = {
    positions: Float64Array.from(mesh.positions),
    joints: mesh.joints ? Uint16Array.from(mesh.joints) : null,
    weights: mesh.weights ? Float64Array.from(mesh.weights) : null,
    rigid,
  };
  numericMeshes.set(mesh, result);
  return result;
}

function verticesFor(mesh, matrices) {
  const numeric = numericMesh(mesh),
    vertices = new Float64Array(numeric.positions.length),
    rigid = matrices[mesh.node];
  // Fold the invariant bind transform into each bone once per pose. Scalar
  // arithmetic avoids allocating several temporary arrays for every vertex at
  //120Hz; it is the same weighted glTF deformation checked against Three.
  const bones = mesh.jointNodes?.map((node, i) =>
    multiply(multiply(matrices[node], mesh.inverseBindMatrices[i]), mesh.bindMatrix),
  );
  const detached = mesh.bindMode === 'detached' ? multiply(rigid, mesh.bindMatrixInverse) : null;
  const min = [Infinity, Infinity, Infinity],
    max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < vertices.length; i += 3) {
    const x = numeric.positions[i],
      y = numeric.positions[i + 1],
      z = numeric.positions[i + 2];
    let px = 0,
      py = 0,
      pz = 0;
    if (bones) {
      const offset = (i / 3) * 4;
      const rigidBone = numeric.rigid[i / 3];
      if (rigidBone >= 0) {
        const m = bones[rigidBone];
        px = m[0] * x + m[4] * y + m[8] * z + m[12];
        py = m[1] * x + m[5] * y + m[9] * z + m[13];
        pz = m[2] * x + m[6] * y + m[10] * z + m[14];
      } else
        for (let j = 0; j < 4; j++) {
          const w = numeric.weights[offset + j];
          if (!w) continue;
          const m = bones[numeric.joints[offset + j]];
          px += (m[0] * x + m[4] * y + m[8] * z + m[12]) * w;
          py += (m[1] * x + m[5] * y + m[9] * z + m[13]) * w;
          pz += (m[2] * x + m[6] * y + m[10] * z + m[14]) * w;
        }
      if (detached) {
        const dx = px,
          dy = py,
          dz = pz,
          m = detached;
        px = m[0] * dx + m[4] * dy + m[8] * dz + m[12];
        py = m[1] * dx + m[5] * dy + m[9] * dz + m[13];
        pz = m[2] * dx + m[6] * dy + m[10] * dz + m[14];
      }
    } else {
      px = rigid[0] * x + rigid[4] * y + rigid[8] * z + rigid[12];
      py = rigid[1] * x + rigid[5] * y + rigid[9] * z + rigid[13];
      pz = rigid[2] * x + rigid[6] * y + rigid[10] * z + rigid[14];
    }
    vertices[i] = px;
    vertices[i + 1] = py;
    vertices[i + 2] = pz;
    min[0] = Math.min(min[0], px);
    min[1] = Math.min(min[1], py);
    min[2] = Math.min(min[2], pz);
    max[0] = Math.max(max[0], px);
    max[1] = Math.max(max[1], py);
    max[2] = Math.max(max[2], pz);
  }
  const result = { vertices, bounds: { min, max }, indices: mesh.indices };
  meshTopologies.set(result, topologyFor(mesh));
  return result;
}

/** Evaluate original clip tracks, accepted pose blending and physical joint
 * overrides without computing matrices, sockets or skinned triangles. Returns
 * fresh local TRS nodes, or null for unaccepted rigs/nonfinite time. Encounter
 * controllers use this for authored articulation without a second skin pass. */
export function sampleSkyAuthoredNodes(rig, clipName, seconds, transition = null) {
  if (!accepted.has(rig) || !Number.isFinite(seconds)) return null;
  const nodes = rig.nodes.map((node) => ({
    position: [...node.position],
    quaternion: [...node.quaternion],
    scale: [...node.scale],
  }));
  const clip = rig.clips.find((candidate) => candidate.name === clipName);
  if (clip)
    for (const track of clip.tracks)
      nodes[track.node][track.property] = sampleTrack(
        track,
        Math.max(0, Math.min(clip.duration, seconds)),
      );
  if (
    transition &&
    poses.has(transition.pose) &&
    transition.pose.nodes.length === nodes.length &&
    Number.isFinite(transition.weight)
  ) {
    const weight = Math.max(0, Math.min(1, transition.weight));
    nodes.forEach((node, i) => {
      for (const property of Object.keys(lengths))
        node[property] = blend(
          node[property],
          transition.pose.nodes[i][property],
          weight,
          property === 'quaternion',
        );
    });
  }
  // Encounter-authorized joint locks operate on these same local transforms.
  // Reject malformed overrides individually; no renderer-only deformation exists.
  if (Array.isArray(transition?.overrides))
    for (const override of transition.overrides) {
      if (
        !indexOf(override.node, nodes.length) ||
        !vector(override.position, 3) ||
        !vector(override.quaternion, 4, 1) ||
        Math.abs(Math.hypot(...override.quaternion) - 1) > 0.001 ||
        !vector(override.scale, 3, 1000) ||
        !override.scale.every((v) => v > 0)
      )
        continue;
      nodes[override.node] = {
        position: [...override.position],
        quaternion: [...override.quaternion],
        scale: [...override.scale],
      };
    }
  return nodes;
}

/** Seek one authored clip in seconds, clamped to its endpoints. Local TRS,
 * measured skinned vertices and socket positions share these exact matrices.
 * Unknown clips deliberately use the rest pose; callers select/loop clips.
 * Optional transform maps asset metres into world metres, without art rescaling.
 * An optional accepted previous pose and0–1weight blend local TRS before both
 * skinning and rendering, so transition smoothing cannot change hit geometry. */
export function sampleSkyAuthoredPose(rig, clipName, seconds, transform = null, transition = null) {
  if (!accepted.has(rig) || !Number.isFinite(seconds)) return null;
  if (
    transform &&
    (!vector(transform.position, 3) ||
      !vector(transform.quaternion, 4, 1) ||
      Math.abs(Math.hypot(...transform.quaternion) - 1) > 0.001 ||
      !vector(transform.scale, 3, 1000) ||
      !transform.scale.every((v) => v > 0))
  )
    return null;
  const nodes = sampleSkyAuthoredNodes(rig, clipName, seconds, transition);
  const root = transform ? compose(transform) : identity(),
    matrices = [];
  nodes.forEach((node, i) => {
    const parent = rig.nodes[i].parent;
    matrices.push(
      multiply(parent === null || parent === -1 ? root : matrices[parent], compose(node)),
    );
  });
  const meshes = rig.meshes.map((mesh) => verticesFor(mesh, matrices));
  const bounds = { min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] };
  for (const mesh of meshes)
    for (let axis = 0; axis < 3; axis++) {
      bounds.min[axis] = Math.min(bounds.min[axis], mesh.bounds.min[axis]);
      bounds.max[axis] = Math.max(bounds.max[axis], mesh.bounds.max[axis]);
    }
  const sockets = Object.fromEntries(
    rig.sockets.map(({ name, node }) => [
      name,
      { x: matrices[node][12], y: matrices[node][13], z: matrices[node][14] },
    ]),
  );
  const pose = { nodes, matrices, meshes, bounds, sockets };
  poses.add(pose);
  return pose;
}

function intersects(from, delta, bounds, limit, radius = 0) {
  let low = 0,
    high = limit;
  for (let axis = 0; axis < 3; axis++) {
    if (Math.abs(delta[axis]) < 1e-12) {
      if (from[axis] < bounds.min[axis] - radius || from[axis] > bounds.max[axis] + radius)
        return false;
    } else {
      const a = (bounds.min[axis] - radius - from[axis]) / delta[axis],
        b = (bounds.max[axis] + radius - from[axis]) / delta[axis];
      low = Math.max(low, Math.min(a, b));
      high = Math.min(high, Math.max(a, b));
      if (low > high) return false;
    }
  }
  return true;
}

/** Earliest double-sided segment hit on actual posed triangles. Mesh bounds
 * reject misses only; empty holes remain empty and rear sockets cannot bypass
 * an intervening surface. An optional mesh-index list restricts physical spectral
 * sections; omitted means every source mesh. Returns world metres, segment
 * fraction, mesh index and triangle ordinal within that mesh. Optional radius
 * sweeps a physical sphere analytically against triangle faces/edges/corners;
 * positive-radius results are sphere centres at contact, not invented hit planes. */
export function skyAuthoredPoseContact(pose, from, to, meshIndices = null, radius = 0) {
  if (
    !poses.has(pose) ||
    !Number.isFinite(radius) ||
    radius < 0 ||
    ![from, to].every((p) => p && ['x', 'y', 'z'].every((axis) => Number.isFinite(p[axis])))
  )
    return null;
  const a = [from.x, from.y, from.z],
    d = [to.x - from.x, to.y - from.y, to.z - from.z];
  let closest = Infinity,
    meshIndex = -1,
    triangleIndex = -1;
  pose.meshes.forEach((mesh, index) => {
    if (meshIndices && !meshIndices.includes(index)) return;
    if (!intersects(a, d, mesh.bounds, Math.min(1, closest), radius)) return;
    const v = mesh.vertices,
      ids = mesh.indices;
    for (const i of candidates(mesh, a, d, Math.min(1, closest), radius)) {
      const ai = ids[i] * 3,
        bi = ids[i + 1] * 3,
        ci = ids[i + 2] * 3;
      if (radius > 0) {
        let outside = false;
        for (let axis = 0; axis < 3; axis++) {
          if (
            Math.max(a[axis], a[axis] + d[axis]) + radius <
              Math.min(v[ai + axis], v[bi + axis], v[ci + axis]) ||
            Math.min(a[axis], a[axis] + d[axis]) - radius >
              Math.max(v[ai + axis], v[bi + axis], v[ci + axis])
          ) {
            outside = true;
            break;
          }
        }
        if (outside) continue;
        // Scalar bounds admission avoids constructing a second whole-model graph
        // of triangle objects for each nearby pilot sweep. Exact face/edge/corner
        // solving runs only on triangles inside the swept sphere's local box.
        const vertex = (offset) => ({ x: v[offset], y: v[offset + 1], z: v[offset + 2] });
        const contact = skyLeviathanTriangleSweep(
          from,
          to,
          { a: vertex(ai), b: vertex(bi), c: vertex(ci) },
          radius,
        );
        if (contact && contact.fraction < closest) {
          closest = contact.fraction;
          meshIndex = index;
          triangleIndex = i / 3;
        }
        continue;
      }
      const ex = v[bi] - v[ai],
        ey = v[bi + 1] - v[ai + 1],
        ez = v[bi + 2] - v[ai + 2];
      const fx = v[ci] - v[ai],
        fy = v[ci + 1] - v[ai + 1],
        fz = v[ci + 2] - v[ai + 2];
      const px = d[1] * fz - d[2] * fy,
        py = d[2] * fx - d[0] * fz,
        pz = d[0] * fy - d[1] * fx;
      const determinant = ex * px + ey * py + ez * pz;
      if (Math.abs(determinant) < 1e-12) continue;
      const tx = a[0] - v[ai],
        ty = a[1] - v[ai + 1],
        tz = a[2] - v[ai + 2];
      const u = (tx * px + ty * py + tz * pz) / determinant;
      if (u < 0 || u > 1) continue;
      const qx = ty * ez - tz * ey,
        qy = tz * ex - tx * ez,
        qz = tx * ey - ty * ex;
      const w = (d[0] * qx + d[1] * qy + d[2] * qz) / determinant;
      if (w < 0 || u + w > 1) continue;
      const t = (fx * qx + fy * qy + fz * qz) / determinant;
      if (t >= 0 && t <= 1 && t < closest) {
        closest = t;
        meshIndex = index;
        triangleIndex = i / 3;
      }
    }
  });
  return meshIndex < 0
    ? null
    : {
        x: a[0] + d[0] * closest,
        y: a[1] + d[1] * closest,
        z: a[2] + d[2] * closest,
        fraction: closest,
        meshIndex,
        triangleIndex,
      };
}
